/**
 * Outgoing work that must survive the app being closed.
 *
 * Everything this app sends was a fire-and-forget promise: a read receipt, a
 * handshake after a session rebuild, a request to resend a message, the
 * message itself. Each one had the same failure: `wsService.send` drops what
 * it cannot deliver, quietly, because there is nowhere to put it — so the work
 * simply did not happen, and nothing anywhere said so. The outbox fixed that
 * for one case; this is the general shape of it, which is what Signal's
 * JobManager is.
 *
 * A job is persisted before it is attempted, retried with backoff, and removed
 * only when it actually succeeded. Jobs in the same `queue` run in order, one
 * at a time — a rebuild handshake must not overtake the message that provoked
 * it.
 *
 * Handlers throw to mean "not done, try again". Returning means done.
 *
 * There is a third answer, and it took losing a message to notice it was
 * missing: "the conditions for this job are not met, and no number of retries
 * will change that". A message held back because the recipient has a device
 * nobody has confirmed is not failing — it is waiting for a person. Counting
 * that as an attempt spends the retry budget on a decision the app cannot
 * make, and the message is dropped while the banner asking for the decision is
 * still on screen. A handler says this by throwing an error with `hold` set;
 * the job keeps its attempts and comes back later.
 */

const JOBS_KEY = '__jobs__';
const DEFAULT_MAX_ATTEMPTS = 8;
const BASE_BACKOFF_MS = 2000;
const MAX_BACKOFF_MS = 5 * 60 * 1000;

/**
 * A held job waits to be released, it does not poll.
 *
 * Retrying every half-minute looked harmless and was not: each attempt asks
 * the server for the bundles of every device it has no session with, and
 * fetching a bundle CONSUMES one of that device's one-time prekeys
 * (routes/keys.js). The one device with no session is exactly the one nobody
 * confirmed — so the polling would have drained a hundred prekeys an hour from
 * it and tripped the server's own limiter, to re-ask a question only a person
 * can answer. Signal does not retry either: it records the mismatch, marks the
 * message failed, and re-sends only when somebody accepts the new key.
 *
 * The long backstop is not for that decision. It is for the case where the key
 * gets trusted somewhere else entirely — the TOFU store is shared with calls
 * (ADR-014), so verifying a person by voice trusts their device without ever
 * touching the chat banner, and the message should eventually notice.
 */
const HOLD_RETRY_MS = 6 * 60 * 60 * 1000;

export function backoffFor(attempts, base = BASE_BACKOFF_MS, max = MAX_BACKOFF_MS) {
  return Math.min(base * Math.pow(2, Math.max(0, attempts - 1)), max);
}

/**
 * "Not now, and not my fault" — the job stays, its attempts untouched.
 *
 * `holdMs` is how long before it is worth looking again; the thing that ends
 * the wait usually arrives from elsewhere (a person pressing Confirm) and
 * kicks the queue directly, so this is only a floor.
 */
export function holdError(message, holdMs = HOLD_RETRY_MS) {
  const e = new Error(message);
  e.hold = true;
  e.holdMs = holdMs;
  return e;
}

export class JobQueue {
  /**
   * `store` is anything with getValue/setValue. `handlers` maps a job type to
   * an async function of (payload, job). `canRun` says whether it is worth
   * trying at all — offline, there is no point burning attempts.
   */
  constructor({ store, handlers = {}, canRun = () => true, now = () => Date.now() }) {
    this.store = store;
    this.handlers = handlers;
    this.canRun = canRun;
    this._now = now;
    this._running = false;
    this._timer = null;
    this._seq = 0;
  }

  async _load() {
    const raw = await this.store.getValue(JOBS_KEY);
    return Array.isArray(raw?.jobs) ? raw.jobs : [];
  }

  async _save(jobs) {
    await this.store.setValue(JOBS_KEY, { jobs });
  }

  /** Persist a job. It is on disk before anything is attempted. */
  async enqueue(type, payload, { queue = 'default', maxAttempts = DEFAULT_MAX_ATTEMPTS, dedupeKey = null } = {}) {
    const jobs = await this._load();
    if (dedupeKey && jobs.some(j => j.dedupeKey === dedupeKey)) return null;
    const job = {
      id: `${this._now()}-${this._seq++}`,
      type, payload, queue, maxAttempts, dedupeKey,
      attempts: 0, nextAt: 0, createdAt: this._now(),
    };
    jobs.push(job);
    await this._save(jobs);
    return job;
  }

  async pending() { return this._load(); }

  /**
   * The thing a held job was waiting for has happened — let it go now.
   *
   * This is the other half of `hold`: the wait ends on an event, not on a
   * timer, and the event is somebody pressing a button. Without this the
   * message would sit there until the backstop expired, which is hours after
   * the person did the one thing that was being asked of them.
   */
  async release(queue) {
    const jobs = await this._load();
    let woken = 0;
    for (const j of jobs) {
      if ((queue == null || j.queue === queue) && j.held) {
        j.held = false; j.nextAt = 0; woken += 1;
      }
    }
    if (woken) await this._save(jobs);
    return woken;
  }

  /** Everything of one kind still waiting — the screen shows these as unsent. */
  async pendingOfType(type) {
    return (await this._load()).filter(j => j.type === type);
  }

  /**
   * Drop a job because the thing it was for has happened by another route.
   *
   * A message is acknowledged by its echo, not by the socket accepting the
   * frame, so the job that sent it is removed when that echo arrives.
   */
  /** The job filed under this key, if it is still waiting — its payload carries the row. */
  async find(dedupeKey) { return (await this._load()).find(j => j.dedupeKey === dedupeKey) || null; }

  async remove(dedupeKey) {
    const jobs = await this._load();
    const left = jobs.filter(j => j.dedupeKey !== dedupeKey);
    if (left.length !== jobs.length) await this._save(left);
    return jobs.length - left.length;
  }

  /**
   * One pass over everything that is due.
   *
   * Serialised per queue: the first job of a queue that fails stops that queue
   * for this pass, so order is kept. Other queues carry on.
   */
  async runOnce() {
    if (this._running) return { ran: 0, failed: 0 };
    this._running = true;
    try {
      if (!this.canRun()) return { ran: 0, failed: 0, skipped: true };
      let jobs = await this._load();
      if (!jobs.length) return { ran: 0, failed: 0 };

      const now = this._now();
      const blocked = new Set();
      const done = new Set();
      let ran = 0, failed = 0, held = 0;

      for (const job of jobs) {
        if (blocked.has(job.queue)) continue;
        if (job.nextAt > now) { blocked.add(job.queue); continue; }
        const handler = this.handlers[job.type];
        if (!handler) {
          // A job nobody can run is not a job. Dropping it loudly beats
          // retrying it until the end of time.
          console.warn('[JOBS] no handler for', job.type, '— dropping');
          done.add(job.id);
          continue;
        }
        try {
          await handler(job.payload, job);
          done.add(job.id);
          ran += 1;
        } catch (e) {
          // Waiting on a person is not a failed attempt. The job stands still
          // — same attempts, same place in its queue — and the queue behind it
          // waits too: a later message must not overtake one that is held
          // because an unconfirmed key is in the conversation.
          if (e && e.hold) {
            held += 1;
            job.held = true;
            job.nextAt = now + (e.holdMs || HOLD_RETRY_MS);
            blocked.add(job.queue);
            continue;
          }
          job.held = false;
          failed += 1;
          job.attempts += 1;
          if (job.attempts >= job.maxAttempts) {
            console.warn('[JOBS]', job.type, 'gave up after', job.attempts, 'attempts:', e?.message);
            done.add(job.id);
          } else {
            job.nextAt = now + backoffFor(job.attempts);
            console.warn('[JOBS]', job.type, 'failed (', e?.message, ') — attempt',
              job.attempts, 'of', job.maxAttempts, ', next in',
              Math.round(backoffFor(job.attempts) / 1000), 's');
          }
          blocked.add(job.queue);
        }
      }

      jobs = jobs.filter(j => !done.has(j.id));
      await this._save(jobs);
      return { ran, failed, held, left: jobs.length };
    } finally {
      this._running = false;
    }
  }

  /** Run now, and keep running on a timer. Safe to call more than once. */
  start(intervalMs = 15000) {
    this.stop();
    this.runOnce().catch(() => {});
    this._timer = setInterval(() => { this.runOnce().catch(() => {}); }, intervalMs);
  }

  stop() {
    if (this._timer) clearInterval(this._timer);
    this._timer = null;
  }
}
